import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authAPI, formatApiError } from '../services/api';
import { EnvelopeIcon, LockClosedIcon, ExclamationCircleIcon, EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      return toast.error("Format email tidak valid");
    }

    setLoading(true);
    try {
      const response = await authAPI.forgotPassword({ email });
      toast.success(response.data.message || 'OTP telah dikirim ke email Anda!');
      setStep(2);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Masukkan 6 digit OTP');
    if (newPassword.length < 6) return toast.error('Password minimal 6 karakter');
    if (newPassword !== confirmPassword) return toast.error('Konfirmasi password tidak cocok');

    setLoading(true);
    try {
      const response = await authAPI.resetPassword({
        email,
        otp,
        new_password: newPassword
      });
      toast.success(response.data.message || 'Password berhasil diubah!');
      navigate('/login');
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-white p-8 rounded-3xl shadow-soft">
          <button onClick={() => step === 1 ? navigate('/login') : setStep(1)} className="flex items-center text-slate-500 hover:text-slate-800 transition-colors mb-6 text-sm font-medium gap-2">
            <ArrowLeftIcon className="w-4 h-4" /> Kembali
          </button>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Pemulihan Password</h2>
          <p className="text-slate-600 mb-8">
            {step === 1 ? 'Masukkan email yang terdaftar pada akun Anda.' : 'Masukkan kode OTP dan buat password baru Anda.'}
          </p>

          {step === 1 ? (
            <form onSubmit={handleRequestOtp} className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">Email Terdaftar</label>
                <div className="relative">
                  <EnvelopeIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@email.com"
                    className="w-full pl-12 pr-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" required />
                </div>
              </div>

              <button type="submit" disabled={loading || !email}
                className="w-full bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Memproses...' : 'Kirim Kode OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-4">Kode OTP telah dikirim ke <strong>{email}</strong>.</p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <div className="space-y-4 mt-6">
                <div>
                  <label className="text-sm font-semibold text-slate-600 mb-2 block">Password Baru</label>
                  <div className="relative">
                    <LockClosedIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                    <input type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimal 6 karakter"
                      className="w-full pl-12 pr-12 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-600 mb-2 block">Konfirmasi Password Baru</label>
                  <div className="relative">
                    <LockClosedIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                    <input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Ulangi password baru"
                      className="w-full pl-12 pr-12 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" required />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={loading || otp.length !== 6 || newPassword.length < 6}
                className="w-full mt-2 bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Menyimpan...' : 'Atur Ulang Password'}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
